import clsx from 'clsx';
import type { ReactNode } from 'react';

import { Icon } from '@gitbook/spine-icons';

import { Disclosure, DisclosureChevron, DisclosurePanel, DisclosureTrigger } from '../Disclosure';
import type { IconDefinition } from '../util';
import { SidebarItemList } from './Sidebar';
import { SidebarHeadingButton } from './SidebarHeading';
import { SidebarButton, SidebarItemIcon } from './SidebarItem';

interface SidebarDisclosureMenuBaseProps {
    label: string;
    isExpanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    children: ReactNode;
    /** Whether the toggle chevron is always visible, or only in active states and while collapsed. */
    toggleVisibility?: 'always' | 'active';
}

export interface SidebarDisclosureMenuItemProps extends SidebarDisclosureMenuBaseProps {
    /**
     * Item-style disclosure menus behave like navigational sidebar rows.
     * This is the default rendering for a `SidebarDisclosureMenu`.
     */
    kind?: 'item';
    icon: IconDefinition;
}

export interface SidebarDisclosureMenuSectionProps extends SidebarDisclosureMenuBaseProps {
    /** Section-style disclosure menus behave like compact sidebar headings. */
    kind: 'section';
    /** Content displayed at the end of the heading row. */
    trailing?: ReactNode;
    className?: string;
}

export type SidebarDisclosureMenuProps =
    | SidebarDisclosureMenuItemProps
    | SidebarDisclosureMenuSectionProps;

/**
 * Sidebar disclosure menu for parent toggles that reveal nested sidebar content.
 */
export function SidebarDisclosureMenu(props: SidebarDisclosureMenuProps) {
    const { label, isExpanded, children, onExpandedChange, toggleVisibility = 'always' } = props;
    const hasSectionTrailing = props.kind === 'section' && props.trailing != null;
    const toggleClassName = clsx(
        'transition-[opacity,scale,rotate,translate]',
        toggleVisibility === 'active' &&
            isExpanded && [
                'opacity-0',
                'group-hover/sidebar-disclosure-menu:opacity-100',
                'group-data-[focus-visible]/sidebar-disclosure-menu:opacity-100',
                hasSectionTrailing && [
                    'group-hover/sidebar-section:opacity-100',
                    'group-focus-within/sidebar-section:opacity-100',
                ],
            ]
    );

    if (props.kind === 'section') {
        const heading = (
            <DisclosureTrigger>
                <SidebarHeadingButton
                    label={
                        <span className="inline-flex min-w-0 items-center">
                            <span className="truncate">{label}</span>
                            <DisclosureChevron className={clsx('ml-2 size-4', toggleClassName)} />
                        </span>
                    }
                    className={clsx(
                        'group/sidebar-disclosure-menu',
                        hasSectionTrailing ? 'min-w-0 flex-1' : 'w-full'
                    )}
                />
            </DisclosureTrigger>
        );

        return (
            <Disclosure isExpanded={isExpanded} onExpandedChange={onExpandedChange}>
                <SidebarItemList fullWidth className={props.className}>
                    {hasSectionTrailing ? (
                        <div className="group/sidebar-section flex items-center rounded-sm focus-within:bg-standout-side hover:bg-standout-side">
                            {heading}
                            <div className="flex shrink-0">{props.trailing}</div>
                        </div>
                    ) : (
                        heading
                    )}
                    <DisclosurePanel contentClassName="flex flex-col gap-px">
                        {children}
                    </DisclosurePanel>
                </SidebarItemList>
            </Disclosure>
        );
    }

    // If no `kind` is specified, render an 'item' disclosure menu.
    return (
        <Disclosure isExpanded={isExpanded} onExpandedChange={onExpandedChange}>
            <DisclosureTrigger>
                <SidebarButton
                    label={label}
                    icon={props.icon}
                    className="group/sidebar-disclosure-menu data-[pressed=true]:bg-neutral-surface-subtle! [&[aria-expanded=true]:not(:hover)]:bg-neutral-surface-subtle!"
                >
                    <span
                        className={clsx(
                            'ml-auto flex transition-transform duration-150 ease-out',
                            toggleClassName,
                            isExpanded && 'rotate-90'
                        )}
                    >
                        <SidebarItemIcon icon={Icon.ChevronRight} />
                    </span>
                </SidebarButton>
            </DisclosureTrigger>
            <DisclosurePanel>
                <div className="ml-[0.875rem] flex flex-col gap-px border-l border-neutral-subtle pl-3">
                    {children}
                </div>
            </DisclosurePanel>
        </Disclosure>
    );
}
